'use client';

import { useState } from 'react';
import { Phone, Calendar, FileText, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../atomic/Button';
import { AppointmentForm } from './AppointmentForm';
import { motion, AnimatePresence } from 'framer-motion';

interface CallToActionSectionProps {
  className?: string;
  gemeindeName?: string;
}

export function CallToActionSection({ className = '', gemeindeName }: CallToActionSectionProps) {
  const phoneNumber = '0512 40 50 60 70';
  const contactPerson = 'Stefan Seiger';
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  
  const handleCallClick = () => {
    window.location.href = 'tel:051240506070';
  };

  const handleScheduleClick = () => {
    setShowAppointmentForm(!showAppointmentForm);
  };

  const steps = [
    {
      number: 1,
      icon: Phone,
      title: 'Kostenloses Beratungsgespräch',
      duration: '15 Min',
      description: 'Klären Sie Ihre Fragen in einem unverbindlichen Erstgespräch'
    },
    {
      number: 2,
      icon: FileText,
      title: 'Unverbindliche Machbarkeitsstudie',
      duration: '',
      description: 'Detaillierte Analyse des Potenzials Ihrer Gemeinde'
    },
    {
      number: 3,
      icon: Users,
      title: 'Präsentation im Gemeinderat',
      duration: '',
      description: 'Professionelle Vorstellung der Ergebnisse und nächsten Schritte'
    }
  ];

  return (
    <>
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6 ${className}`}>
      <div className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-4 sm:p-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
          Überzeugt? So geht's weiter:
        </h2>
        <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
          Starten Sie jetzt Ihre Energiegemeinschaft
        </p>
      </div>

      {/* Desktop view - traditional layout */}
      <div className="hidden md:block space-y-5 mb-8">
        {steps.map((step) => (
          <div key={step.number} className="flex items-start gap-5">
            <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-base aspect-square">
              {step.number}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-3 mb-2">
                <step.icon className="h-5 w-5 text-green-600" />
                {step.title}
                {step.duration && (
                  <span className="text-sm text-gray-500 font-medium">({step.duration})</span>
                )}
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile view - accordion layout */}
      <div className="md:hidden space-y-3 mb-8">
        {steps.map((step) => (
          <div key={step.number} className="bg-white rounded-xl shadow-sm border border-gray-100">
            <button
              onClick={() => setExpandedStep(expandedStep === step.number ? null : step.number)}
              className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-base aspect-square">
                {step.number}
              </div>
              <div className="flex-1 text-left">
                <div className="flex items-center gap-3 mb-1">
                  <step.icon className="h-6 w-6 text-green-600" />
                  <h3 className="font-semibold text-base text-gray-900">
                    {step.title}
                  </h3>
                </div>
                {step.duration && (
                  <span className="text-sm text-gray-500 font-medium">({step.duration})</span>
                )}
              </div>
              {expandedStep === step.number ? (
                <ChevronUp className="h-6 w-6 text-gray-400" />
              ) : (
                <ChevronDown className="h-6 w-6 text-gray-400" />
              )}
            </button>
            
            <AnimatePresence>
              {expandedStep === step.number && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <p className="text-base text-gray-600 px-4 pb-4 leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <Button
          onClick={handleScheduleClick}
          className={`w-full bg-green-600 hover:bg-green-700 text-white py-4 text-lg font-bold transition-all shadow-lg ${showAppointmentForm ? 'ring-2 ring-green-600 ring-offset-2' : ''}`}
        >
          <Calendar className="h-6 w-6 mr-3" />
          {showAppointmentForm ? 'FORMULAR SCHLIESSEN' : 'JETZT TERMIN VEREINBAREN'}
        </Button>

        {/* Appointment Form slides down here */}
        <AppointmentForm 
          isOpen={showAppointmentForm}
          onClose={() => setShowAppointmentForm(false)}
          gemeindeName={gemeindeName}
        />

        {/* Mobile-optimized call section */}
        <div className="text-center bg-green-50 md:bg-transparent rounded-lg p-4 md:p-0">
          <p className="text-sm text-gray-600 mb-2">Oder direkt anrufen:</p>
          <button
            onClick={handleCallClick}
            className="text-lg sm:text-xl font-bold text-green-600 hover:text-green-700 transition-colors"
          >
            <Phone className="inline-block h-5 w-5 mr-2" />
            {phoneNumber}
          </button>
          <p className="text-sm text-gray-600 mt-1">
            {contactPerson}
          </p>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="text-green-600">✓</span>
            Kostenlos
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="text-green-600">✓</span>
            Unverbindlich
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="text-green-600">✓</span>
            Kompetent
          </div>
        </div>
      </div>
      </div>
    </div>
    </>
  );
}